import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Spinner, Switch } from "@chakra-ui/react";
import { Button } from "newui/components/Button";
import { TextInputWithLabel } from "newui/components/ui/InputWithLabel/TextInputWithLabel";
import { useCurrentUser } from "hooks/auth";
import { setTwoFactorSetupRequired } from "newui/features/Auth/authSlice";
import { showToast } from "utils/helpers";
import {
	useCompany2faSettings,
	useUpdateCompany2faSettings,
} from "../../hooks/useCompany2faSettings";
import { COMPANY_2FA_MAX_TIMEOUT_HOURS } from "../../types/Mfa.type";

type TimeoutUnit = "days" | "hours";

const HOURS_PER_DAY = 24;
const MAX_DAYS = COMPANY_2FA_MAX_TIMEOUT_HOURS / HOURS_PER_DAY; // 30

// Prefer showing whole days when the stored hours divide evenly, else hours.
const deriveUnit = (hours: number | null): TimeoutUnit =>
	hours === null || (hours !== null && hours % HOURS_PER_DAY === 0) ? "days" : "hours";

const toDisplayValue = (hours: number | null, unit: TimeoutUnit): string => {
	if (hours === null) {
		return "";
	}
	return unit === "days" ? String(hours / HOURS_PER_DAY) : String(hours);
};

export const Company2faPolicySection = () => {
	const { data: settings, isLoading } = useCompany2faSettings();
	const updateSettings = useUpdateCompany2faSettings();
	const dispatch = useDispatch();
	const { currentUser } = useCurrentUser() || {};

	const [isEnforced, setIsEnforced] = useState(false);
	const [unit, setUnit] = useState<TimeoutUnit>("days");
	const [timeoutValue, setTimeoutValue] = useState<string>("");
	const [validationError, setValidationError] = useState<string | null>(null);

	useEffect(() => {
		if (settings) {
			const nextUnit = deriveUnit(settings.device_timeout_hours);
			setIsEnforced(settings.is_2fa_enforced);
			setUnit(nextUnit);
			setTimeoutValue(toDisplayValue(settings.device_timeout_hours, nextUnit));
		}
	}, [settings]);

	if (isLoading || !settings) {
		return (
			<div className="p-6" data-testid="company-2fa-policy-loading">
				<Spinner size="sm" /> Loading two-factor authentication policy…
			</div>
		);
	}

	const maxForUnit = unit === "days" ? MAX_DAYS : COMPANY_2FA_MAX_TIMEOUT_HOURS;

	// Switch units, converting the current entered value so it represents the same
	// duration (blank stays blank). Guards against fractional day conversions.
	const handleUnitChange = (nextUnit: TimeoutUnit) => {
		if (nextUnit === unit) {
			return;
		}
		setValidationError(null);
		const trimmed = timeoutValue.trim();
		if (trimmed !== "" && Number.isFinite(Number(trimmed))) {
			const asHours = unit === "days" ? Number(trimmed) * HOURS_PER_DAY : Number(trimmed);
			setTimeoutValue(
				nextUnit === "days"
					? String(Math.round((asHours / HOURS_PER_DAY) * 100) / 100)
					: String(asHours)
			);
		}
		setUnit(nextUnit);
	};

	// Returns the timeout in HOURS (null = default), or false when invalid.
	const validateToHours = (): number | null | false => {
		const trimmed = timeoutValue.trim();

		if (trimmed === "") {
			return null;
		}

		const parsed = Number(trimmed);
		const asHours = unit === "days" ? parsed * HOURS_PER_DAY : parsed;

		if (
			!Number.isInteger(asHours) ||
			asHours < 0 ||
			asHours > COMPANY_2FA_MAX_TIMEOUT_HOURS
		) {
			setValidationError(
				`Enter a whole number of ${unit} between 0 and ${maxForUnit} (max 30 days), or leave blank for the 30-day default. 0 means devices are never remembered.`
			);
			return false;
		}

		return asHours;
	};

	const handleSave = () => {
		setValidationError(null);
		const deviceTimeoutHours = validateToHours();

		if (deviceTimeoutHours === false) {
			return;
		}

		updateSettings.mutate(
			{ is_2fa_enforced: isEnforced, device_timeout_hours: deviceTimeoutHours },
			{
				onSuccess: () => {
					showToast("Two-factor policy updated", "Success", true);
					// Reflect the new policy on the current admin instantly: if they now
					// fall under enforcement and haven't enrolled, the app-shell gate
					// opens immediately (no extra request).
					dispatch(
						setTwoFactorSetupRequired(
							isEnforced && !currentUser?.two_factor_enabled
						)
					);
				},
				onError: () => showToast("Failed to update two-factor policy", "Error"),
			}
		);
	};

	return (
		<div className="flex max-w-lg flex-col gap-6 p-6" data-testid="company-2fa-policy">
			<div className="flex items-start justify-between gap-4">
				<div>
					<p className="font-roboto text-sm font-semibold text-gray-800">
						Require two-factor authentication (2FA)
					</p>
					<p className="font-roboto text-sm text-gray-600">
						When enabled, every user in your company must set up 2FA before they can use the
						platform.
					</p>
				</div>
				<Switch
					isChecked={isEnforced}
					onChange={(e) => setIsEnforced(e.target.checked)}
					data-testid="enforce-2fa-switch"
				/>
			</div>

			<div className="flex flex-col gap-2">
				<div className="flex items-end gap-3">
					<TextInputWithLabel
						label={`Remember this device for (${unit})`}
						type="number"
						min={0}
						max={maxForUnit}
						placeholder={unit === "days" ? String(MAX_DAYS) : String(COMPANY_2FA_MAX_TIMEOUT_HOURS)}
						fixedPlaceholder
						value={timeoutValue}
						onChange={(e) => setTimeoutValue(e.target.value)}
						errorText={validationError ?? undefined}
						testId="device-timeout-input"
						noMargin
					/>
					<div className="mb-1 flex overflow-hidden rounded-md border border-gray-300" role="group">
						<Button
							type="button"
							btntype={unit === "days" ? "primary" : "base"}
							onClick={() => handleUnitChange("days")}
							data-testid="timeout-unit-days"
						>
							Days
						</Button>
						<Button
							type="button"
							btntype={unit === "hours" ? "primary" : "base"}
							onClick={() => handleUnitChange("hours")}
							data-testid="timeout-unit-hours"
						>
							Hours
						</Button>
					</div>
				</div>
				<p className="font-roboto text-sm text-gray-600">
					How long a trusted device can skip the 2FA challenge. Leave blank for the default of 30
					days. Maximum 30 days ({COMPANY_2FA_MAX_TIMEOUT_HOURS} hours).
				</p>
				<p className="font-roboto text-sm text-gray-600">
					Set to 0 to disable remembering devices.
				</p>
			</div>

			<div>
				<Button
					type="button"
					btntype="primary"
					onClick={handleSave}
					disabled={updateSettings.isPending}
					data-testid="save-2fa-policy-btn"
				>
					{updateSettings.isPending ? (
						<div className="flex items-center gap-2">
							<Spinner size="sm" /> Saving
						</div>
					) : (
						"Save policy"
					)}
				</Button>
			</div>
		</div>
	);
};
